import { cleanTranscript, MAX_TRANSCRIPT_CHARS } from '@fos/shared/contracts/meetings';
import { Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { Button } from '../../../ui/Button';
import { Field } from '../../../ui/Field';
import { Textarea } from '../../../ui/Input';

/** Rozszerzenia plików transkrypcji (tekst, napisy z Meet / Teams / Zoom). */
export const TRANSCRIPT_EXTENSIONS = ['txt', 'vtt', 'srt'] as const;
const MAX_FILE_BYTES = 5 * 1024 * 1024;

/**
 * Wczytanie transkrypcji z pliku po stronie przeglądarki (nic nie trafia na serwer przed zapisem formularza):
 * .txt bez zmian, .vtt / .srt bez znaczników czasu i numerów napisów (`cleanTranscript`).
 */
export async function readTranscriptFile(file: File): Promise<{ text: string } | { error: string }> {
  const ext = file.name.toLowerCase().split('.').pop() ?? '';
  if (!(TRANSCRIPT_EXTENSIONS as readonly string[]).includes(ext)) {
    return { error: 'Wybierz plik .txt, .vtt albo .srt.' };
  }
  if (file.size > MAX_FILE_BYTES) return { error: 'Plik jest za duży (najwyżej 5 MB).' };
  const raw = await file.text();
  const text = cleanTranscript(raw, file.name);
  if (!text.trim()) return { error: 'Plik nie zawiera tekstu.' };
  if (text.length > MAX_TRANSCRIPT_CHARS) return { error: 'Transkrypcja jest za długa (najwyżej 300 tys. znaków).' };
  return { text };
}

/** Pole „Transkrypcja”: wklej tekst albo wczytaj plik .txt / .vtt / .srt. */
export function TranscriptField({
  value,
  onChange,
  error,
  rows = 6,
  autoFocus = false,
}: {
  value: string;
  onChange: (value: string) => void;
  error?: string | null;
  rows?: number;
  autoFocus?: boolean;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setFileError(null);
    const res = await readTranscriptFile(file);
    if ('error' in res) {
      setFileError(res.error);
      return;
    }
    setFileName(file.name);
    onChange(res.text);
  };

  return (
    <Field
      label="Transkrypcja"
      error={error ?? fileError}
      hint={fileName ? `Wczytano z pliku ${fileName}.` : 'Wklej tekst albo wczytaj plik .txt, .vtt lub .srt.'}
      labelAction={
        <>
          <input
            ref={fileRef}
            type="file"
            accept=".txt,.vtt,.srt,text/plain,text/vtt"
            className="sr-only"
            tabIndex={-1}
            aria-label="Plik z transkrypcją"
            onChange={(e) => {
              void onFile(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          <Button type="button" variant="ghost" size="s" icon={Upload} onClick={() => fileRef.current?.click()}>
            Wczytaj z pliku
          </Button>
        </>
      }
    >
      <Textarea
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={'Paweł 16:42: Ekran pomocy jest super…\nSeban 16:43: Możemy zrobić dwa kroki…'}
        className="font-normal"
      />
    </Field>
  );
}
